// use-work-detail-actions v2: 作品详情页操作管理Hook
// 2026-04-17 改造：移除 ad-hoc login modal + pendingSaveAction state，
// 统一改用 useAuthGuard 提供登录守卫；所有写操作通过 requireLogin() 拦截。

import { useCallback } from 'react';

import { useAuth } from '../../../context/AuthContext';
import useAuthGuard from '../../../hooks/useAuthGuard';
import {
  favoritesService,
  commentService,
  notificationService,
} from '../../../services';
import { useNavigation } from '../../../utils/navigation';
import { getArtistById } from '../../artist-profile/utils/artistHelpers';
import { LoginModal } from '../../ui/LoginModal';

/**
 * 作品详情页操作管理Hook
 * @param {Object} state - 状态对象
 * @param {Object} setters - 设置函数对象
 * @returns {Object} 操作函数
 */
const useWorkDetailActions = ({ state, setters }) => {
  const { goBack, navigateToArtist, navigateToWork } = useNavigation();
  const { user: currentUser } = useAuth();
  const {
    requireLogin,
    isLoginModalOpen,
    handleLoginSuccess,
    handleClose: handleLoginModalClose,
  } = useAuthGuard();

  // 处理返回按钮点击
  const handleBackClick = useCallback(() => {
    goBack();
  }, [goBack]);

  // 处理点赞按钮点击
  const handleLikeClick = useCallback(() => {
    setters.toggleLike();
  }, [setters]);

  // 处理收藏按钮点击（未登录时弹登录窗，不执行收藏）
  const handleSaveClick = useCallback(
    async (itemType, itemId, shouldFavorite) => {
      // 第一道防线：前端登录守卫
      if (!requireLogin()) return;

      console.log('[Favorites] Save toggle payload:', {
        itemType,
        itemId,
        shouldFavorite,
      });

      try {
        const result = await favoritesService.toggleFavorite(
          itemType,
          itemId,
          shouldFavorite
        );
        console.log('[Favorites] Save toggle result:', result);

        // 第二道防线：后端仍返回 needAuth（如 session 过期）时也弹登录窗
        if (result.needAuth) {
          console.log(
            '[Favorites] Authentication required, showing login modal'
          );
          requireLogin();
          return;
        }

        if (result.success) {
          setters.toggleSave();
          console.log('[Favorites] Save toggle successful');
        } else {
          console.error('[Favorites] Save toggle failed:', result.error);
        }
      } catch (error) {
        console.error('[Favorites] Failed to toggle favorite:', error);
      }
    },
    [setters, requireLogin]
  );

  // 处理评论提交
  const handleCommentSubmit = useCallback(async () => {
    if (!state.comment.trim()) {
      console.warn('[WorkDetail] Comment is empty');
      return;
    }

    // 登录守卫：未登录弹窗（2026-04-17 改造）
    if (!requireLogin()) return;

    if (!currentUser) {
      console.warn('[WorkDetail] No current user found');
      return;
    }

    try {
      // 获取用户的真实profile数据
      const authorProfile = await getArtistById(currentUser.id);
      const authorName = authorProfile?.name || currentUser.name;

      const result = await commentService.createComment(
        state.workData.id,
        currentUser.id,
        authorName, // 使用profile中的真实姓名
        state.comment.trim(),
        state.replyTo // 传递父评论ID（如果是回复）
      );

      if (result.success) {
        console.log('[WorkDetail] Comment created successfully:', result.data);

        // 创建评论通知（只有当评论者不是作品作者时）
        if (currentUser.id !== state.workData.author.id) {
          try {
            await notificationService.createCommentNotification(
              currentUser.id,
              authorName,
              state.workData.id,
              state.workData.title,
              state.workData.author.id,
              state.comment.trim()
            );
            console.log(
              '[WorkDetail] Comment notification created successfully'
            );
          } catch (notificationError) {
            console.warn(
              '[WorkDetail] Failed to create comment notification:',
              notificationError
            );
            // 通知创建失败不影响评论功能
          }
        }

        // 清空评论输入
        setters.setComment('');
        // 取消回复模式
        setters.setReplyTo(null);
        // 重新加载评论列表
        setters.loadComments();
      } else {
        console.error('[WorkDetail] Failed to create comment:', result.error);
      }
    } catch (error) {
      console.error('[WorkDetail] Error creating comment:', error);
    }
  }, [
    setters,
    state.comment,
    state.workData?.id,
    state.replyTo,
    state.workData?.author?.id,
    state.workData?.title,
    requireLogin,
    currentUser,
  ]);

  // 处理回复提交
  const handleSubmitReply = useCallback(
    async parentId => {
      const text = (state.replyDrafts[parentId] || '').trim();
      if (!text) return;

      // 登录守卫：未登录弹窗（2026-04-17 改造）
      if (!requireLogin()) return;

      if (!currentUser) {
        console.warn('[WorkDetail] No current user found for reply');
        return;
      }

      const parent = state.comments.find(c => c.id === parentId);
      if (!parent) {
        console.warn('[WorkDetail] Parent comment not found');
        return;
      }

      // 获取用户的真实profile数据
      const authorProfile = await getArtistById(currentUser.id);
      const authorName = authorProfile?.name || currentUser.name;

      const tempId = `tmp_${Date.now()}`;

      // 1) 乐观插入
      const optimistic = {
        id: tempId,
        workId: parent.workId,
        authorId: currentUser.id,
        authorName: authorName,
        text: text,
        parentId: parent.id,
        rootId: parent.rootId || parent.id,
        likes: 0,
        replies: [],
        isDeleted: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      setters.addCommentOptimistic(optimistic);

      try {
        // 2) 调服务
        const result = await commentService.createComment(
          parent.workId,
          currentUser.id,
          authorName,
          text,
          parent.id
        );

        if (result.success) {
          // 3) 用真实数据替换临时数据
          setters.replaceTempComment({ tempId, saved: result.data });
          setters.setReplyDraft(parentId, '');
          setters.cancelReply();
          console.log('[WorkDetail] Reply submitted successfully');
        } else {
          // 4) 失败回滚
          setters.removeTempComment(tempId);
          console.error('[WorkDetail] Failed to submit reply:', result.error);
          // 这里可以添加Toast错误提示
        }
      } catch (error) {
        // 4) 失败回滚
        setters.removeTempComment(tempId);
        console.error('[WorkDetail] Error submitting reply:', error);
        // 这里可以添加Toast错误提示
      }
    },
    [setters, state.replyDrafts, state.comments, requireLogin, currentUser]
  );

  // 处理开始回复（未登录弹窗，拦截回复输入框的打开）
  const handleStartReply = useCallback(
    parent => {
      if (!requireLogin()) return;
      setters.startReply(parent);
    },
    [setters, requireLogin]
  );

  // 处理取消回复
  const handleCancelReply = useCallback(() => {
    setters.cancelReply();
  }, [setters]);

  // 处理设置回复草稿
  const handleSetReplyDraft = useCallback(
    (parentId, text) => {
      setters.setReplyDraft(parentId, text);
    },
    [setters]
  );

  // 处理评论输入变化
  const handleCommentChange = useCallback(
    e => {
      setters.setComment(e.target.value);
    },
    [setters]
  );

  // 处理作者点击
  const handleAuthorClick = useCallback(() => {
    // 使用作品的作者ID进行导航
    const authorId = state.workData?.author?.id;
    if (authorId) {
      console.log(`[WorkDetail] Navigating to artist: ${authorId}`);
      navigateToArtist(authorId, 'gallery', 'Works');
    } else {
      console.warn('[WorkDetail] No author ID found for navigation');
    }
  }, [navigateToArtist, state.workData?.author?.id]);

  // 处理评论用户点击
  const handleCommentUserClick = useCallback(
    userId => {
      navigateToArtist(userId);
    },
    [navigateToArtist]
  );

  // 处理相关作品点击
  const handleRelatedWorkClick = useCallback(
    workId => {
      if (workId) {
        console.log('[WorkDetail] Navigating to related work:', workId);
        navigateToWork(workId);
      }
    },
    [navigateToWork]
  );

  // 处理图片导航
  const handleImageNavigation = useCallback(direction => {
    // 这里可以添加图片导航逻辑
    console.log('Image navigation:', direction);
  }, []);

  // 处理评论点赞（未登录弹窗）
  const handleCommentLike = useCallback(
    async commentId => {
      if (!requireLogin()) return;

      if (!currentUser) {
        console.warn('[WorkDetail] No current user found for comment like');
        return;
      }

      try {
        const result = await commentService.likeComment(
          commentId,
          currentUser.id
        );
        if (result.success) {
          console.log('[WorkDetail] Comment like toggled:', result.data);
          setters.loadComments();
        } else {
          console.error('[WorkDetail] Failed to like comment:', result.error);
        }
      } catch (error) {
        console.error('[WorkDetail] Error liking comment:', error);
      }
    },
    [setters, requireLogin, currentUser]
  );

  // 处理评论回复（未登录弹窗）
  const handleCommentReply = useCallback(
    commentId => {
      if (!requireLogin()) return;
      setters.setReplyTo(commentId);
      console.log('[WorkDetail] Reply to comment:', commentId);
    },
    [setters, requireLogin]
  );

  // 处理评论删除
  const handleCommentDelete = useCallback(
    async commentId => {
      if (!currentUser) {
        console.warn('[WorkDetail] No current user found for comment delete');
        return;
      }

      try {
        const result = await commentService.deleteComment(
          commentId,
          currentUser.id
        );
        if (result.success) {
          console.log('[WorkDetail] Comment deleted successfully');
          // 重新加载评论列表
          setters.loadComments();
        } else {
          console.error('[WorkDetail] Failed to delete comment:', result.error);
        }
      } catch (error) {
        console.error('[WorkDetail] Error deleting comment:', error);
      }
    },
    [setters]
  );

  return {
    // 导航操作
    handleBackClick,
    handleAuthorClick,
    handleCommentUserClick,
    handleRelatedWorkClick,

    // 交互操作
    handleLikeClick,
    handleSaveClick,
    handleCommentSubmit,
    handleCommentChange,
    handleCommentLike,
    handleCommentReply,
    handleCommentDelete,
    handleCancelReply,
    handleImageNavigation,

    // 回复相关操作
    handleStartReply,
    handleSubmitReply,
    handleSetReplyDraft,

    // 登錄 Modal 相關（现由 useAuthGuard 提供状态）
    isLoginModalOpen,
    handleLoginModalClose,
    handleLoginSuccess,
    // 暴露 requireLogin，以便 WorkDetailPage_refactored.js 可以包装
    // likeCountState.toggleLike 和 followCountState.toggleFollow 等外部 handler
    requireLogin,
    LoginModalComponent: () => (
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    ),
  };
};

export default useWorkDetailActions;
